import { extractApiError, type HttpApiClient } from "@vana/core";
import { redirectIfUnauthorized, SessionExpiredError } from "../../../../auth/session-expired";
import { build } from "../../../../infrastructure/build";
import { fetchZoneOptions } from "../../../../shared/geo/coverage-repo";
import { type ZoneOption } from "../../../../shared/geo/zone-option";
import {
  type ComplianceCase,
  type CustomersPage,
  type ExportCaseParams,
  type ExportedCase,
  type RecordAccessParams,
  type AddAddressParams,
  type RegisterInternalDeliveryParams,
  type UpdateAddressParams,
  type RecordCustodyParams,
  type ReplaceCaptureParams,
  type RevealedNationalId,
  type SearchCustomersParams,
} from "../../domain/entities/compliance";
import {
  ExportCaseError,
  GetCaseError,
  RecordAccessError,
  RegisterInternalDeliveryError,
  ListGeoCatalogError,
  AddAddressError,
  UpdateAddressError,
  RecordCustodyError,
  ReplaceCaptureError,
  RevealNationalIdError,
  SearchCustomersError,
} from "../../domain/errors/errors";
import { type ComplianceRepository } from "../../domain/repos/compliance-repo";
import {
  type AccessLogResponse,
  type CaseResponse,
  type CustodyResponse,
  type CustomersPageResponse,
  type RevealResponse,
} from "../../infrastructure/responses/compliance-responses";

const emptyPage = (page: number, pageSize: number): CustomersPage => ({
  items: [],
  page,
  pageSize,
  totalItems: 0,
});

export class ComplianceRepo implements ComplianceRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async searchCustomers(params: SearchCustomersParams): Promise<CustomersPage> {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;
    try {
      const qs = new URLSearchParams();
      if (params.query.trim()) qs.set("q", params.query.trim());
      qs.set("page", String(page));
      qs.set("page_size", String(pageSize));
      const response = await this.apiClient.get<CustomersPageResponse>(
        `v1/compliance/customers?${qs.toString()}`,
      );
      return response.data ?? emptyPage(page, pageSize);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new SearchCustomersError(message, apiError);
    }
  }

  async getCase(requestId: string): Promise<ComplianceCase> {
    try {
      const response = await this.apiClient.get<CaseResponse>(
        `v1/compliance/cases/${requestId}`,
      );
      return response.data as ComplianceCase;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetCaseError(message, apiError);
    }
  }

  async revealNationalId(requestId: string): Promise<RevealedNationalId> {
    try {
      const response = await this.apiClient.post<RevealResponse>(
        `v1/compliance/cases/${requestId}/national-id`,
        {},
      );
      return response.data as RevealedNationalId;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RevealNationalIdError(message, apiError);
    }
  }

  async recordAccess(params: RecordAccessParams): Promise<void> {
    try {
      await this.apiClient.post<AccessLogResponse>(`v1/compliance/access-log`, {
        action: params.action,
        requestId: params.requestId,
        payload: params.payload,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RecordAccessError(message, apiError);
    }
  }

  async registerInternalDelivery(params: RegisterInternalDeliveryParams): Promise<void> {
    try {
      // No body at all: the request is in the path, the actor comes from the
      // verified session (DMS-10), and the only state this writes is delivered.
      await this.apiClient.post(`v1/requests/${params.requestId}/internal-delivery`, {});
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RegisterInternalDeliveryError(message, apiError);
    }
  }

  async addAddress(params: AddAddressParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/addresses`, {
        street: params.street,
        number: params.number,
        neighborhood: params.neighborhood,
        deliveryZoneCode: params.deliveryZoneCode,
        instructions: params.instructions || undefined,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new AddAddressError(message, apiError);
    }
  }

  async updateAddress(params: UpdateAddressParams): Promise<void> {
    try {
      await this.apiClient.patch(
        `v1/requests/${params.requestId}/addresses/${params.addressId}`,
        {
          street: params.street,
          number: params.number,
          neighborhood: params.neighborhood,
          deliveryZoneCode: params.deliveryZoneCode,
          // Not `|| undefined`: "" is the erase, and dropping it would turn
          // "remove the indications" into "leave them alone".
          instructions: params.instructions,
        },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UpdateAddressError(message, apiError);
    }
  }

  async listGeoCatalog(): Promise<ZoneOption[]> {
    try {
      // The whole catalogue, inactive coverage included: an address may sit in a
      // municipio the operation does not serve and still has to be correctable.
      return await fetchZoneOptions(this.apiClient, { includeInactive: true });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListGeoCatalogError(message, apiError);
    }
  }

  async recordCustody(params: RecordCustodyParams): Promise<void> {
    try {
      // An empty shelf is sent as an empty string, which the backend treats as
      // "none". No actor: it comes from the verified session (DMS-10).
      await this.apiClient.post<CustodyResponse>(`v1/requests/${params.requestId}/custody`, {
        box: params.box,
        folder: params.folder,
        shelf: params.shelf ?? "",
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RecordCustodyError(message, apiError);
    }
  }

  async replaceCapture(params: ReplaceCaptureParams): Promise<void> {
    // Multipart: sent through fetch rather than the JSON-shaped axios client, so
    // the browser sets the boundary and the body is never re-encoded. `page` is
    // explicit because omitting it appends a page to a multi-page contract
    // instead of replacing the one on screen.
    try {
      const form = new FormData();
      form.append("type", params.captureType);
      form.append("page", String(params.page));
      form.append("file", params.file, params.file.name);

      const response = await fetch(`${build.apiHost}/v1/requests/${params.requestId}/captures`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!response.ok) throw new Error(`replace failed (${response.status})`);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReplaceCaptureError(message, apiError);
    }
  }

  async exportCase(params: ExportCaseParams): Promise<ExportedCase> {
    // Binary response: the axios client is JSON-shaped, so this one goes through
    // fetch with the session cookie. Still through the BFF, never to storage.
    let response: Response;
    try {
      response = await fetch(`${build.apiHost}/v1/compliance/cases/${params.requestId}/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          include_full_national_id: params.includeFullNationalId,
          include_history: params.includeHistory,
        }),
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ExportCaseError(message, apiError);
    }
    // Session gone (DMS-10 gate): the redirect to login is already on its way.
    // Thrown OUTSIDE the ExportCaseError wrapping so the caller can tell it
    // apart and skip the generic export_error toast.
    if (redirectIfUnauthorized(response.status)) throw new SessionExpiredError();
    try {
      if (!response.ok) throw new Error(`export failed (${response.status})`);
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const match = /filename="?([^";]+)"?/.exec(disposition);
      return { blob: await response.blob(), fileName: match?.[1] ?? `expediente-${params.requestId}.pdf` };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ExportCaseError(message, apiError);
    }
  }

}
